/**
 * 60秒ポーリングで /api/posts を取得するフック。
 *
 * ポストは TTL 30日で低頻度更新のため、参加者位置（30秒）より緩い間隔にする。
 * usePins と同じく、タブ非表示中はポーリングを停止し、アンマウント時に中断する。
 *
 * 要件 23-9 に対応。
 */
import { useEffect, useRef, useState } from 'react';
import { fetchPosts, type PostsResponse } from '../api/client';

const POLL_INTERVAL_MS = 60_000;

export interface UsePostsResult {
  data: PostsResponse | null;
  error: Error | null;
  loading: boolean;
  /** 手動で即時再取得する（投稿・削除の直後にマップへ反映するため）。 */
  refresh: () => void;
}

export function usePosts(): UsePostsResult {
  const [data, setData] = useState<PostsResponse | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [loading, setLoading] = useState(true);

  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  /** 最新の load 関数を保持し、refresh から呼べるようにする。 */
  const loadRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    let disposed = false;

    const load = async () => {
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      try {
        const res = await fetchPosts(controller.signal);
        if (!disposed) {
          setData(res);
          setError(null);
        }
      } catch (err) {
        if (!disposed && (err as Error).name !== 'AbortError') {
          setError(err as Error);
        }
      } finally {
        if (!disposed) {
          setLoading(false);
        }
      }
    };
    loadRef.current = () => void load();

    const startPolling = () => {
      if (timerRef.current !== null) {
        return;
      }
      void load();
      timerRef.current = setInterval(() => void load(), POLL_INTERVAL_MS);
    };

    const stopPolling = () => {
      if (timerRef.current !== null) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      abortRef.current?.abort();
    };

    const handleVisibility = () => {
      if (document.hidden) {
        stopPolling();
      } else {
        startPolling();
      }
    };

    if (!document.hidden) {
      startPolling();
    }
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      disposed = true;
      document.removeEventListener('visibilitychange', handleVisibility);
      stopPolling();
    };
  }, []);

  return {
    data,
    error,
    loading,
    refresh: () => loadRef.current?.(),
  };
}
